import { AxiosMethods, AutoRFQApi, RFQApi } from '@setvi/shared/enums';
import { axiosHelper } from '@setvi/shared/services';
import {
  RfqBody,
  SaveAutoRfqItemsBody,
  UpdateRfqBody,
  UploadFilesUrlsBody
} from './types';

export enum AutoRFQMutationsKey {
  CREATE = 'CREATE_AUTO_RFQ',
  UPDATE = 'UPDATE_AUTO_RFQ',
  SAVE_AUTO_RFQ_ITEMS = 'SAVE_AUTO_RFQ_ITEMS',
  UPDATE_AUTO_RFQ_ITEMS = 'UPDATE_AUTO_RFQ_ITEMS',
  DELETE = 'DELETE_AUTO_RFQ',
  FETCH = 'FETCH_AUTO_RFQ_ITEMS',
  UPLOAD_FILES_URLS = 'UPLOAD_FILES_URLS',
  FILES_STATUES = 'FILES_STATUES',
  GET_EXPORTED_RFQ = 'GET_EXPORTED_RFQ',
  SAVE_CUSTOMER_NOTE = 'SAVE_RFQ_CUSTOMER_NOTE',
  DELETE_CUSTOMER_NOTE = 'DELETE_RFQ_CUSTOMER_NOTE',
  CREATE_INTERNAL_NOTE = 'CREATE_RFQ_INTERNAL_NOTE',
  UPDATE_INTERNAL_NOTE = 'UPDATE_RFQ_INTERNAL_NOTE',
  DELETE_INTERNAL_NOTE = 'DELETE_RFQ_INTERNAL_NOTE'
}

export const createAutoRfqMutation = () => ({
  mutationKey: [AutoRFQMutationsKey.CREATE],
  mutationFn: (body: RfqBody) =>
    axiosHelper({
      endpoint: AutoRFQApi.Create,
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body
    })
});

export const uploadFilesUrlsMutation = () => ({
  mutationKey: [AutoRFQMutationsKey.UPLOAD_FILES_URLS],
  mutationFn: (body: UploadFilesUrlsBody) =>
    axiosHelper({
      endpoint: RFQApi.GetFilesQueue,
      method: AxiosMethods.POST,
      baseURL: process.env.INTELLIGENCE_API_BASE_URL,
      body
    })
});

export const getFilesStatusesMutation = () => ({
  mutationKey: [AutoRFQMutationsKey.FILES_STATUES],
  mutationFn: (rfqId: string) =>
    axiosHelper({
      endpoint: RFQApi.GetFilesStatuses,
      method: AxiosMethods.GET,
      baseURL: process.env.INTELLIGENCE_API_BASE_URL,
      headers: {
        Accept: 'text/plain'
      },
      params: { rfqId }
    })
});

export const updateAutoRfqMutation = () => ({
  mutationKey: [AutoRFQMutationsKey.UPDATE],
  mutationFn: ({ id, body }: UpdateRfqBody) =>
    axiosHelper({
      endpoint: AutoRFQApi.Update.replace(':id', id),
      method: AxiosMethods.PUT,
      baseURL: process.env.REVOPS_API,
      body
    })
});

export const saveAutoRfqItemsMutation = () => ({
  mutationKey: [AutoRFQMutationsKey.SAVE_AUTO_RFQ_ITEMS],
  mutationFn: ({ rfqId, products }: SaveAutoRfqItemsBody) =>
    axiosHelper({
      endpoint: AutoRFQApi.SaveItems.replace(':id', rfqId),
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body: {
        products
      }
    })
});

export const updateAutoRfqItemsMutation = () => ({
  mutationKey: [AutoRFQMutationsKey.UPDATE_AUTO_RFQ_ITEMS],
  mutationFn: ({ rfqId, products }: SaveAutoRfqItemsBody) =>
    axiosHelper({
      endpoint: AutoRFQApi.UpdateItems.replace(':id', rfqId),
      method: AxiosMethods.PUT,
      baseURL: process.env.REVOPS_API,
      body: {
        products
      }
    })
});

export const deleteRfqMutation = () => ({
  mutationKey: [AutoRFQMutationsKey.DELETE],
  mutationFn: (id: string | number) =>
    axiosHelper({
      endpoint: AutoRFQApi.DeleteSingle.replace(':id', id.toString()),
      baseURL: process.env.REVOPS_API,
      method: AxiosMethods.DELETE
    })
});

export const saveCustomerNoteMutation = () => ({
  mutationKey: [AutoRFQMutationsKey.SAVE_CUSTOMER_NOTE],
  mutationFn: ({ rfqItemId, note }: { rfqItemId: string; note: string }) =>
    axiosHelper({
      endpoint: AutoRFQApi.CustomerNote.replace(':rfqItemId', rfqItemId),
      method: AxiosMethods.PUT,
      baseURL: process.env.REVOPS_API,
      body: { note }
    })
});

export const deleteCustomerNoteMutation = () => ({
  mutationKey: [AutoRFQMutationsKey.DELETE_CUSTOMER_NOTE],
  mutationFn: ({ rfqItemId }: { rfqItemId: string }) =>
    axiosHelper({
      endpoint: AutoRFQApi.CustomerNote.replace(':rfqItemId', rfqItemId),
      method: AxiosMethods.DELETE,
      baseURL: process.env.REVOPS_API
    })
});

export const createInternalNoteMutation = () => ({
  mutationKey: [AutoRFQMutationsKey.CREATE_INTERNAL_NOTE],
  mutationFn: ({
    rfqItemId,
    content
  }: {
    rfqItemId: string;
    content: string;
  }) =>
    axiosHelper({
      endpoint: AutoRFQApi.InternalNotes.replace(':rfqItemId', rfqItemId),
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body: { content }
    })
});

export const updateInternalNoteMutation = () => ({
  mutationKey: [AutoRFQMutationsKey.UPDATE_INTERNAL_NOTE],
  mutationFn: ({
    rfqItemId,
    noteId,
    content
  }: {
    rfqItemId: string;
    noteId: string;
    content: string;
  }) =>
    axiosHelper({
      endpoint: AutoRFQApi.InternalNote.replace(
        ':rfqItemId',
        rfqItemId
      ).replace(':noteId', noteId),
      method: AxiosMethods.PUT,
      baseURL: process.env.REVOPS_API,
      body: { content }
    })
});

export const deleteInternalNoteMutation = () => ({
  mutationKey: [AutoRFQMutationsKey.DELETE_INTERNAL_NOTE],
  mutationFn: ({ rfqItemId, noteId }: { rfqItemId: string; noteId: string }) =>
    axiosHelper({
      endpoint: AutoRFQApi.InternalNote.replace(
        ':rfqItemId',
        rfqItemId
      ).replace(':noteId', noteId),
      method: AxiosMethods.DELETE,
      baseURL: process.env.REVOPS_API
    })
});

export const getExportedRFQMutation = () => ({
  mutationKey: [AutoRFQMutationsKey.GET_EXPORTED_RFQ],
  mutationFn: (params: {
    statusId: string;
    exportFileMode: 'AugmentedOriginal' | 'RfqFormat';
  }) =>
    axiosHelper({
      endpoint: RFQApi.GetExportedFile,
      method: AxiosMethods.GET,
      baseURL: process.env.INTELLIGENCE_API_BASE_URL,
      params,
      responseType: 'blob', // Treat response as a binary file
      headers: {
        Accept: 'text/plain', // Expect CSV response
        'content-disposition': 'attachment'
      }
    })
});
